/**
 * Admin Resources API Service
 * HTTP client methods for resource content management endpoints
 */

import { apiClient } from '@/core/api/client';
import type { PaginatedResponse } from '../types/pagination.type';
import type {
  AdminResource,
  CreateResourcePayload,
  UpdateResourcePayload,
  ResourceListParams,
  ResourceType,
} from '../types/admin-resource.type';

const BASE_PATH = '/admin/resources';

// Simple category type for API responses
type CategoryOption = {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
};

/**
 * List resources with pagination and comprehensive filters
 * GET /admin/resources
 * 
 * @param params - Query parameters (page, pageSize, search, filters)
 * @returns Paginated list of resources with total count reflecting filters
 */
export async function listResources(
  params?: ResourceListParams,
): Promise<PaginatedResponse<AdminResource>> {
  const response = await apiClient.get<PaginatedResponse<AdminResource>>(
    BASE_PATH,
    { params },
  );
  return response.data;
}

/**
 * Get single resource by ID
 * GET /admin/resources/:id
 * 
 * @param id - Resource ID
 * @returns Resource details
 */
export async function getResource(id: string): Promise<AdminResource> {
  const response = await apiClient.get<AdminResource>(`${BASE_PATH}/${id}`);
  return response.data;
}

/**
 * Create new resource
 * POST /admin/resources
 * 
 * @param payload - Resource data
 * @returns Created resource
 */
export async function createResource(
  payload: CreateResourcePayload,
): Promise<AdminResource> {
  const response = await apiClient.post<AdminResource>(BASE_PATH, payload);
  return response.data;
}

/**
 * Update existing resource
 * PATCH /admin/resources/:id
 * 
 * @param id - Resource ID
 * @param payload - Partial resource data to update
 * @returns Updated resource
 */
export async function updateResource(
  id: string,
  payload: UpdateResourcePayload,
): Promise<AdminResource> {
  const response = await apiClient.patch<AdminResource>(
    `${BASE_PATH}/${id}`,
    payload,
  );
  return response.data;
}

/**
 * Soft-delete resource
 * DELETE /admin/resources/:id
 * 
 * @param id - Resource ID
 * @returns void (204 No Content)
 */
export async function softDeleteResource(id: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${id}`);
}

/**
 * Get all resource types
 * GET /resources/types
 * 
 * @returns List of resource types
 */
export async function getResourceTypes(): Promise<ResourceType[]> {
  const response = await apiClient.get<ResourceType[]>('/resources/types');
  return response.data;
}

/**
 * Get all resource categories
 * GET /resources/categories
 * 
 * @returns List of categories
 */
export async function getResourceCategories(): Promise<CategoryOption[]> {
  const response = await apiClient.get<CategoryOption[]>('/resources/categories');
  return response.data;
}

/**
 * Default export for convenient importing
 */
export const adminResourcesApi = {
  listResources,
  getResource,
  createResource,
  updateResource,
  softDeleteResource,
  getResourceTypes,
  getResourceCategories,
};
